import React from 'react';
import { CheckCircle2Icon, MessageCircleIcon, TruckIcon, XIcon } from 'lucide-react';
import { OrderBuilderState } from './useOrderBuilder';
import { formatMXN } from '../../utils/currency';
interface OrderSummaryProps {
  order: OrderBuilderState;
}
const inputClass = 'mt-1.5 block h-12 w-full min-w-0 appearance-none rounded-md border-2 bg-white px-3.5 text-navy-900 placeholder:text-steel-400 focus:border-accent-500 focus:outline-none';
export function OrderSummary({
  order
}: OrderSummaryProps) {
  const {
    lines,
    subtotal,
    totalPieces,
    remainingForFree,
    freeDelivery,
    progress,
    setQty,
    factura,
    setFactura,
    ciudad,
    setCiudad,
    attempted,
    errors,
    send
  } = order;
  return <div id="resumen-pedido" className="rounded-xl bg-navy-900 p-4 text-white sm:p-6" data-id="element-183" data-mp="394cgrsnx54kq">
      <div className="flex items-baseline justify-between gap-3" data-id="element-184" data-mp="22pl6iycwo7e1">
        <h3 className="font-display text-xl font-semibold" data-id="element-185" data-mp="1xp5uca4d15xn">Tu pedido</h3>
        {totalPieces > 0 && <span className="text-sm text-steel-400" data-id="element-186" data-mp="83ugnkrh9pwo">{totalPieces} piezas</span>}
      </div>

      {lines.length === 0 ? <p className="mt-3 rounded-md border border-dashed border-white/15 px-4 py-4 text-sm text-steel-200" data-id="element-187" data-mp="c44utkpfbmmm">
          Suma piezas con los botones <span className="font-semibold text-white" data-id="element-188" data-mp="3ucvn7mu8rotb">+</span> y aquí verás tu total al
          instante.
        </p> : <ul className="mt-2 max-h-56 divide-y divide-white/10 overflow-y-auto" data-id="element-189" data-mp="3kl8181ggezz8">
          {lines.map(line => <li key={line.item.id} className="flex items-center gap-2 py-2.5 text-sm" data-id="element-190" data-mp="3pc7umwqd6zwz">
              <div className="min-w-0 flex-1" data-id="element-191" data-mp="3bkjn76r28zmc">
                <p className="truncate font-medium text-white" data-id="element-192" data-mp="3bluqz6kia7dg">
                  {line.qty} × {line.item.name}
                </p>
                <p className="truncate text-steel-400" data-id="element-193" data-mp="3gh0do7lqfq2l">{line.item.spec}</p>
              </div>
              <span className="shrink-0 whitespace-nowrap font-semibold text-white" data-id="element-194" data-mp="31ogbb96epmm7">{formatMXN(line.total)}</span>
              <button type="button" onClick={() => setQty(line.item.id, 0)} aria-label={`Quitar ${line.item.name} ${line.item.spec}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-steel-400 transition-colors duration-150 ease-out hover:text-white" data-id="element-195" data-mp="2nglgztkmzx72">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-196" data-mp="28t8r5snsr5av" />
              </button>
            </li>)}
        </ul>}

      <div className="mt-3 flex items-end justify-between gap-3 border-t border-white/10 pt-3" data-id="element-197" data-mp="31gcyw5e9xxqc">
        <span className="text-sm text-steel-200" data-id="element-198" data-mp="2wh9t2iqgznhb">Total estimado</span>
        <span className="whitespace-nowrap font-display text-3xl font-bold leading-none text-white" data-id="element-199" data-mp="2wff6z13gppbn">
          {formatMXN(subtotal)}
        </span>
      </div>

      {/* Meta de entrega sin costo */}
      <div className="mt-4 rounded-lg bg-navy-800 p-3" aria-live="polite" data-id="element-200" data-mp="1l3edj8vnlcpk">
        <div className="flex items-start gap-2 text-sm" data-id="element-201" data-mp="1g1yvzqsopobx">
          {freeDelivery ? <>
              <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" data-id="element-202" data-mp="3rrofykhg22di" />
              <span className="font-semibold text-emerald-300" data-id="element-203" data-mp="klpxwdce24v">¡Tu entrega va sin costo!</span>
            </> : <>
              <TruckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" aria-hidden="true" data-id="element-204" data-mp="a9vjzhrqxuvm" />
              <span className="text-steel-200" data-id="element-205" data-mp="f8ynuyvzpv20">
                Te faltan <strong className="text-white" data-id="element-206" data-mp="f8f4reult0me">{formatMXN(remainingForFree)}</strong> para entrega sin costo
              </span>
            </>}
        </div>
        <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-white/10" data-id="element-207" data-mp="o9dca7493q9q">
          <div className={`h-full rounded-full transition-[width] duration-300 ease-out ${freeDelivery ? 'bg-emerald-400' : 'bg-accent-500'}`} style={{
          width: `${Math.max(progress * 100, lines.length ? 4 : 0)}%`
        }} data-id="element-208" data-mp="t8hl2wn9jlzj" />
        </div>
      </div>

      {/* Solo lo mínimo para cotizar: ciudad y factura (ambos opcionales) */}
      <div className="mt-5 space-y-3" data-id="element-209" data-mp="pdna1tedi31p">
        <div data-id="element-210" data-mp="kdiovq2isk55">
          <label htmlFor="order-city" className="block text-sm font-medium text-steel-200" data-id="element-211" data-mp="kbxtxzcamz6r">
            Ciudad de entrega <span className="font-normal text-steel-400" data-id="element-212" data-mp="ybywrkc4cgov">(opcional)</span>
          </label>
          <input id="order-city" autoComplete="address-level2" value={ciudad} onChange={e => setCiudad(e.target.value)} placeholder="Ej. León" className={`${inputClass} border-transparent`} data-id="element-213" data-mp="uc88podz8eo1" />
        </div>

        <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md bg-navy-800 px-3.5 text-sm text-white" data-id="element-214" data-mp="feo6gp8gsxlk">
          <input type="checkbox" checked={factura} onChange={e => setFactura(e.target.checked)} className="h-5 w-5 shrink-0 accent-accent-500" data-id="element-215" data-mp="almno6550dwp" />
          Necesito factura
        </label>
      </div>

      {attempted && errors.items && <p role="alert" className="mt-3 text-sm text-red-300" data-id="element-216" data-mp="34vwafani4mbd">
          {errors.items}
        </p>}

      <button type="button" onClick={send} className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md bg-accent-500 px-4 text-base font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-accent-600 active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500" data-id="element-217" data-mp="39q0m277ppiis">
        <MessageCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-218" data-mp="3dprh4n49ukt4" />
        <span className="whitespace-nowrap" data-id="element-219" data-mp="3drvjh0mivbra">Enviar pedido por WhatsApp</span>
      </button>
      <p className="mt-2 text-center text-xs text-steel-400" data-id="element-220" data-mp="3iqozazpa7eum">Se abre WhatsApp con tu pedido ya escrito. Sin compromiso.</p>
    </div>;
}